import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { translate } from '@jsverse/transloco';
import { capitalize } from 'humanize-plus';
import { Subject, takeUntil } from 'rxjs';
import { DropdownObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { Constants } from 'app/constants/constants';
import { ModalCloseType } from 'app/enums';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { Comparison, Widget } from 'app/providers/bridge-api/dashboard.service';
import {
  AccountMetadata,
  StorageAccountService,
} from 'app/providers/storage-api/blender-account.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { ExtractFormControl } from 'app/utils/validator.utils';
import { WidgetDataSource } from '../../../../models';
import { NumericMetadataFieldSelectorComponent } from '../numeric-metadata-field-selector/numeric-metadata-field-selector.component';
import { NumericMetadataField } from '../numeric-metadata-field-selector/numeric-metadata-field-selector.models';
import { MetadataField } from '../widget-add/widget-add.component';
import { WidgetGridOptions } from '../widget-grid/widget-grid.component';
import { GridBreakdownDateConfigComponent } from './grid-breakdown-date-config/grid-breakdown-date-config.component';
import {
  GridBreakdownTaxonomyConfig,
  GridBreakdownTaxonomyConfigComponent,
} from './grid-breakdown-taxonomy-config/grid-breakdown-taxonomy-config.component';

export interface GridAnalyticItemSelectorDialogOptions {
  widget: Widget;
  widgetDataSource: WidgetDataSource;
  isNew: boolean;
  metadataFields: string[];
  sentimentScale: number;
  comparison?: Comparison;
}

export interface GridAnalyticItemSelectorDialogResult {
  numericMetadataFields: NumericMetadataField[];
  breakdownField: string;
  breakdownLabel: string;
  displayField: string;
  includeBlank: boolean;
  useDisplayName: boolean;
  showAll: boolean;
  allLabel: string;
  showTopN: boolean;
  topNLabel: string;
  dateInterval?: string;
  taxonomyOptions?: GridBreakdownTaxonomyConfig;
}

export function setGridAnalyticItemSelectorDialogResultOnPayload(
  payload: WidgetGridOptions,
  result: GridAnalyticItemSelectorDialogResult
): void {
  payload.numericMetadataFields = result.numericMetadataFields;
  payload.breakdownField = result.breakdownField;
  payload.breakdownLabel = result.breakdownLabel;
  payload.displayField = result.displayField;
  payload.includeBlank = result.includeBlank;
  payload.useDisplayName = result.useDisplayName;
  payload.showAll = result.showAll;
  payload.allLabel = result.allLabel;
  payload.showTopN = result.showTopN;
  payload.topNLabel = result.topNLabel;
  payload.dateInterval = result.dateInterval;
  payload.taxonomyOptions = result.taxonomyOptions;
}

interface GridForm {
  breakdownField: FormControl<string>;
  breakdownLabel: FormControl<string>;
  displayField: FormControl<string>;
  includeBlank: FormControl<boolean>;
  useDisplayName: FormControl<boolean>;
  showAll: FormControl<boolean>;
  allLabel: FormControl<string>;
  showTopN: FormControl<boolean>;
  topNLabel: FormControl<string>;
}

type GridFormValue = ExtractFormControl<GridForm>;

@EnableClassTelemetry()
@Component({
  selector: 'app-grid-analytic-item-selector-dialog',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    AppSharedModule,
    NumericMetadataFieldSelectorComponent,
    GridBreakdownDateConfigComponent,
    GridBreakdownTaxonomyConfigComponent,
  ],
  templateUrl: './grid-analytic-item-selector-dialog.component.html',
  styleUrls: ['./grid-analytic-item-selector-dialog.component.scss'],
})
export class GridAnalyticItemSelectorDialogComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() options: GridAnalyticItemSelectorDialogOptions;

  @Output() close = new EventEmitter<
    GridAnalyticItemSelectorDialogResult | null | ModalCloseType
  >();

  form: FormGroup<GridForm>;

  keywordFieldSet: DropdownObject[];
  keywordFieldSetWithType: Array<DropdownObject & { type?: string }> = [];

  numericFields: MetadataField[];
  numericMetadataFields: NumericMetadataField[];
  isNmotFieldSelectorValid: boolean;
  isTaxonomyOptionsValid = true;
  showBreakdownDateConfig = false;
  showTaxonomyPicker = false;

  readonly GRID_TOP_N = Constants.GRID_TOP_N;

  private readonly destroy$ = new Subject<void>();
  dateInterval: string;
  availableDateFields: DropdownObject[] = [];
  taxonomyOptions: GridBreakdownTaxonomyConfig;

  constructor(
    private accountService: StorageAccountService,
    private formBuilder: FormBuilder,
    private authService: AuthService
  ) {
    this.buildForm();
  }

  ngOnInit(): void {
    this.accountService.groupFieldsSub
      .takeUntil(this.destroy$)
      .subscribe((accountMetadata: AccountMetadata) => this.groupFieldSub(accountMetadata));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSave(): void {
    const formValue = this.form.getRawValue() as GridFormValue;

    const result: GridAnalyticItemSelectorDialogResult = {
      ...formValue,
      breakdownLabel:
        formValue.breakdownLabel ||
        this.formatBreakdownLabelText(formValue.breakdownField, formValue.includeBlank),
      allLabel:
        formValue.allLabel || translate('widgets.grid-analytic-item-selector-dialog.totals-all'),
      topNLabel:
        formValue.topNLabel ||
        translate('widgets.grid-analytic-item-selector-dialog.totals-top-n', {
          value: this.GRID_TOP_N,
        }),
      numericMetadataFields: this.numericMetadataFields,
      dateInterval: this.showBreakdownDateConfig && this.dateInterval ? this.dateInterval : null,
      taxonomyOptions: this.showTaxonomyPicker ? this.taxonomyOptions : null,
    };

    this.close.emit(result);
  }

  onCancel(): void {
    this.close.emit(ModalCloseType.CANCEL);
  }

  onNumericMetadataFieldsChanged(fields: NumericMetadataField[]): void {
    this.numericMetadataFields = fields;
  }

  onNumericMetadataFieldsIsValid(isValid: boolean): void {
    this.isNmotFieldSelectorValid = isValid;
  }

  onTaxonomyOptionsValid(isValid: boolean): void {
    this.isTaxonomyOptionsValid = isValid;
  }

  onBreakdownLabelIconClick(): void {
    this.form.controls.breakdownLabel.setValue('');
  }

  get isNoBreakdown(): boolean {
    return this.form.controls.breakdownField.value === Constants.NO_BREAKDOWN_VALUE;
  }

  get isTotalsDisabled(): boolean {
    return !!this.options.comparison && this.isNoBreakdown;
  }

  get totalsDisabledTooltip(): string | null {
    if (!this.isTotalsDisabled) {
      return null;
    }
    return translate(
      'widgets.grid-analytic-item-selector-dialog.totals-disabled-comparison-no-breakdown'
    );
  }

  get isInvalid(): boolean {
    return (
      !this.isNmotFieldSelectorValid ||
      (this.showTaxonomyPicker && !this.isTaxonomyOptionsValid) ||
      this.form.invalid
    );
  }

  private buildForm(): void {
    this.form = this.formBuilder.group(<GridForm>{
      breakdownField: new FormControl(null, Validators.required),
      breakdownLabel: new FormControl('', Validators.required),
      displayField: new FormControl(null),
      includeBlank: new FormControl(true),
      useDisplayName: new FormControl(false),
      showAll: new FormControl(false),
      allLabel: new FormControl(translate('widgets.grid-analytic-item-selector-dialog.totals-all')),
      showTopN: new FormControl(false),
      topNLabel: new FormControl(
        translate('widgets.grid-analytic-item-selector-dialog.totals-top-n', {
          value: this.GRID_TOP_N,
        })
      ),
    });

    this.form.controls.breakdownField.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((breakdownFieldId: string) => this.handleBreakdownFieldChange(breakdownFieldId));

    this.form.controls.includeBlank.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((includeBlank: boolean) => this.handleIncludeBlankChange(includeBlank));

    this.form.controls.breakdownLabel.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((label: string) => this.handleBreakdownLabelChange(label));

    this.form.controls.useDisplayName.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((useDisplayName: boolean) => this.handleUseDisplayNameChange(useDisplayName));
  }

  private handleBreakdownFieldChange(breakdownFieldId: string): void {
    if (breakdownFieldId === Constants.NO_BREAKDOWN_VALUE) {
      this.showBreakdownDateConfig = false;
      this.form.controls.displayField.setValue(null);
      this.form.controls.displayField.clearValidators();
      this.form.controls.displayField.updateValueAndValidity();
      this.form.controls.breakdownLabel.clearValidators();
      this.form.controls.breakdownLabel.setValue(
        translate('widgets.grid-analytic-item-selector-dialog.no-breakdown')
      );
      this.form.controls.breakdownLabel.updateValueAndValidity();
      if (this.options.comparison) {
        this.form.controls.showAll.disable();
        this.form.controls.showTopN.disable();
        this.form.controls.allLabel.disable();
        this.form.controls.topNLabel.disable();
      }
      return;
    }
    this.form.controls.showAll.enable();
    this.form.controls.showTopN.enable();
    this.form.controls.allLabel.enable();
    this.form.controls.topNLabel.enable();
    this.form.controls.breakdownLabel.setValidators(Validators.required);
    this.form.controls.breakdownLabel.updateValueAndValidity();
    this.decideShowBreakdownDateConfig(breakdownFieldId);
    this.form.controls.displayField.setValue(breakdownFieldId);
    this.setBreakdownLabelIfNotDirty(breakdownFieldId, this.form.controls.includeBlank.value);
    this.showTaxonomyPicker = breakdownFieldId === 'taxonomy';
  }

  private decideShowBreakdownDateConfig(breakdownFieldId: string) {
    this.showBreakdownDateConfig =
      this.keywordFieldSetWithType.find((f) => f.id === breakdownFieldId)?.type === 'date';
    this.availableDateFields = this.keywordFieldSetWithType
      .filter((f) => f.type === 'date' && f.id !== breakdownFieldId)
      .map((f) => ({ id: f.id, title: f.title }));
  }

  private handleIncludeBlankChange(includeBlank: boolean): void {
    this.setBreakdownLabelIfNotDirty(this.form.controls.breakdownField.value, includeBlank);
  }

  private handleBreakdownLabelChange(label: string): void {
    if (label === FieldService.getFieldLongName(this.form.controls.breakdownField.value)) {
      this.form.controls.useDisplayName.setValue(true);
    } else {
      this.form.controls.useDisplayName.setValue(false);
    }
  }

  private handleUseDisplayNameChange(useDisplayName: boolean): void {
    const labelControl = this.form.controls.breakdownLabel;
    if (useDisplayName) {
      labelControl.setValue(FieldService.getFieldLongName(this.form.controls.breakdownField.value));
    }
  }

  private groupFieldSub(accountMetadata: AccountMetadata): void {
    const dsMetadataFields = accountMetadata.metadata
      .filter((f) => this.options.metadataFields?.includes(f.fieldName))
      .map((f) => ({
        name: f.fieldName,
        label: f.description,
        type: f.fieldType,
        isRating: f.metadata?.rating === true,
        isRedacted: f.metadata?.redacted === true,
        isDisplayed: f.metadata?.display !== false,
        calculationFieldType: f.metadata.calculationFieldType,
      }));
    if (this.options.widgetDataSource.hasDateData) {
      dsMetadataFields.push({
        type: 'date',
        name: 'createdAt',
        label: translate('analytic-items.createdAt'),
        isRating: false,
        isDisplayed: true,
        isRedacted: false,
        calculationFieldType: undefined,
      });
    }

    this.numericFields = dsMetadataFields.filter(
      (f) => f.isDisplayed && FieldService.hasNumerics(f.type, f.calculationFieldType)
    );
    this.numericFields.push({
      name: 'sentimentScore',
      label: translate('analytic-items.sentiment'),
      type: 'double',
      isDisplayed: true,
      isRating: false,
      isRedacted: false,
    });
    this.keywordFieldSetWithType = this.getKeywordFieldSet(dsMetadataFields);
    this.keywordFieldSet = [...new Set(this.keywordFieldSetWithType)].map((f) => ({
      id: f.id,
      title: f.title,
    }));

    this.keywordFieldSet.push({
      id: 'taxonomy',
      title: translate('widgets.grid-analytic-item-selector-dialog.taxonomy'),
    });
    setTimeout(() => this.loadValuesIfExists(), 200);
  }

  private getKeywordFieldSet(metadataFields: MetadataField[]): DropdownObject[] {
    const standardFields = this.options.widgetDataSource.standardFields
      .filter((f) => Constants.GRID_ALLOWED_STANDARD_FIELDS.includes(f))
      .map((f) => ({ title: capitalize(f), id: f, type: 'standard_field' }));

    const noBreakdownOption: DropdownObject & { type?: string } = {
      id: Constants.NO_BREAKDOWN_VALUE,
      title: translate('widgets.grid-analytic-item-selector-dialog.no-breakdown'),
      type: 'no_breakdown',
    };

    const fields = metadataFields
      .filter(
        (f) =>
          f.isDisplayed &&
          (f.type === 'keyword' || f.type === 'date') &&
          (!f.isRedacted || !this.authService.isAlwaysRedact)
      )
      .map((f) => ({ title: f.label, id: f.name, type: f.type }))
      .concat(standardFields)
      .sort((a, b) => {
        return a.title.toLocaleLowerCase().localeCompare(b.title.toLocaleLowerCase());
      });

    return [noBreakdownOption, ...fields];
  }

  private loadValuesIfExists(): void {
    const payload = this.options.widget.payload as WidgetGridOptions;
    this.dateInterval = payload.dateInterval;

    if (payload.breakdownField) {
      this.form.patchValue({
        breakdownField: payload.breakdownField,
        breakdownLabel: payload.useDisplayName
          ? FieldService.getFieldLongName(payload.breakdownField)
          : payload.breakdownLabel,
        useDisplayName: payload.useDisplayName,
        displayField: payload.displayField,
        includeBlank: payload.includeBlank,
        showAll: payload.showAll,
        allLabel: payload.allLabel,
        showTopN: payload.showTopN,
        topNLabel: payload.topNLabel,
      });
    }
  }

  private setBreakdownLabelIfNotDirty(fieldId: string, includeBlank: boolean): void {
    if (!this.form.controls.breakdownLabel.dirty && !this.form.controls.useDisplayName.value) {
      this.form.controls.breakdownLabel.setValue(
        this.formatBreakdownLabelText(fieldId, includeBlank)
      );
    }
  }

  private formatBreakdownLabelText(fieldId: string, includeBlank: boolean): string {
    const dateIntervalLabel =
      this.showBreakdownDateConfig && this.dateInterval
        ? `(${translate('widgets.datatable.breakdown-by.' + this.dateInterval)})`
        : '';
    return `${FieldService.getFieldLongName(fieldId)} ${dateIntervalLabel} (${
      includeBlank
        ? translate('widgets.grid-analytic-item-selector-dialog.including-blank')
        : translate('widgets.grid-analytic-item-selector-dialog.excluding-blank')
    })`;
  }

  onBreakdownDateConfigChanged(event) {
    this.dateInterval = event;
    if (!this.form.controls.useDisplayName.value) {
      this.form.controls.breakdownLabel.setValue(
        this.formatBreakdownLabelText(
          this.form.controls.breakdownField.value,
          this.form.controls.includeBlank.value
        )
      );
    }
  }

  onTaxonomyOptionsChanged(event: GridBreakdownTaxonomyConfig) {
    this.taxonomyOptions = event;
  }
}
